/**
 * Canvas renderer.
 *
 * Deliberately shape-based rather than sprite-based: the map is semantic tile
 * ids, so dropping in a real tileset later means changing `drawTile` and
 * nothing else. Only the tiles inside the camera are touched each frame.
 */

import {
  PLAYER_RADIUS,
  PROXIMITY_RADIUS,
  TILE_SIZE,
  Tile,
  rectToPixels,
  rectCenterPixels,
  type OfficeMap,
  type Surface,
} from '@virtual-office/shared';

import { characterSprite, walkFrame } from './sprite.js';
import type { ClientPlayer, Store } from './state.js';

const FLOOR_COLORS: Record<number, string> = {
  [Tile.Floor]: '#3b4150',
  [Tile.Carpet]: '#464061',
  [Tile.Wood]: '#4c4239',
  [Tile.Rug]: '#513f4d',
  [Tile.Door]: '#6a5a3d',
};

const DECOR_COLORS: Record<number, string> = {
  [Tile.Desk]: '#7b6249',
  [Tile.Table]: '#8b7053',
  [Tile.Chair]: '#585f71',
  [Tile.Plant]: '#4a7c4f',
  [Tile.Whiteboard]: '#e6e6dc',
  [Tile.NoteBoard]: '#b5843d',
  [Tile.Couch]: '#6d5668',
  [Tile.Screen]: '#26323f',
  [Tile.Counter]: '#7e7367',
};

const WALL_FACE = '#20242c';
const WALL_TOP = '#2b3038';
const WINDOW_GLASS = '#5d82aa';
const VOID_COLOR = '#151820';

/** How dark a room you are not in becomes. */
const ZONE_DIM = 0.34;

export interface RenderContext {
  /** Surface the local player can currently reach, if any. */
  nearbySurface: Surface | null;
  /** Surface whose board panel is open, highlighted more strongly. */
  activeSurfaceId: string | null;
}

export class Renderer {
  private readonly ctx: CanvasRenderingContext2D;
  private width = 0;
  private height = 0;
  private dpr = 1;
  private cameraX = 0;
  private cameraY = 0;
  /** Fitted label sizes, keyed by name and room width. */
  private readonly labelSizes = new Map<string, number>();
  /** Current dim level per zone, eased toward its target each frame. */
  private readonly dimAlpha = new Map<string, number>();
  private lastDimAt = 0;
  /** Distance walked per player, for the walk cycle. */
  private readonly travelled = new Map<string, number>();
  private readonly lastPos = new Map<string, { x: number; y: number }>();

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly store: Store,
  ) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D is not available in this browser.');
    this.ctx = ctx;
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  private resize(): void {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.width = this.canvas.clientWidth || window.innerWidth;
    this.height = this.canvas.clientHeight || window.innerHeight;
    this.canvas.width = Math.round(this.width * this.dpr);
    this.canvas.height = Math.round(this.height * this.dpr);
  }

  /** Convert a world pixel position to a screen position. */
  worldToScreen(x: number, y: number): { x: number; y: number } {
    return { x: x - this.cameraX, y: y - this.cameraY };
  }

  draw(render: RenderContext): void {
    const { ctx } = this;
    const map = this.store.map;

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = VOID_COLOR;
    ctx.fillRect(0, 0, this.width, this.height);
    if (!map) return;

    this.updateCamera(map);
    ctx.translate(-Math.round(this.cameraX), -Math.round(this.cameraY));

    const currentZoneId = this.store.me?.zoneId ?? null;

    this.drawTiles(map);
    this.drawZoneLabels(map, currentZoneId);
    this.drawSurfaces(map, render);
    this.drawProximityRing();
    this.drawPlayers();
    this.drawZoneDimming(map, currentZoneId);

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  private updateCamera(map: OfficeMap): void {
    const worldW = map.width * map.tileSize;
    const worldH = map.height * map.tileSize;
    const me = this.store.me;
    const focusX = me?.renderX ?? worldW / 2;
    const focusY = me?.renderY ?? worldH / 2;

    this.cameraX =
      worldW <= this.width
        ? (worldW - this.width) / 2
        : Math.max(0, Math.min(focusX - this.width / 2, worldW - this.width));
    this.cameraY =
      worldH <= this.height
        ? (worldH - this.height) / 2
        : Math.max(0, Math.min(focusY - this.height / 2, worldH - this.height));
  }

  private drawTiles(map: OfficeMap): void {
    const { ctx } = this;
    const startX = Math.max(0, Math.floor(this.cameraX / TILE_SIZE));
    const startY = Math.max(0, Math.floor(this.cameraY / TILE_SIZE));
    const endX = Math.min(map.width - 1, Math.ceil((this.cameraX + this.width) / TILE_SIZE));
    const endY = Math.min(map.height - 1, Math.ceil((this.cameraY + this.height) / TILE_SIZE));

    for (let ty = startY; ty <= endY; ty++) {
      for (let tx = startX; tx <= endX; tx++) {
        const i = ty * map.width + tx;
        const ground = map.ground[i] ?? Tile.Void;
        const px = tx * TILE_SIZE;
        const py = ty * TILE_SIZE;

        if (ground === Tile.Wall || ground === Tile.Window) {
          ctx.fillStyle = WALL_FACE;
          ctx.fillRect(px, py, TILE_SIZE, TILE_SIZE);
          ctx.fillStyle = WALL_TOP;
          ctx.fillRect(px, py, TILE_SIZE, 6);
          if (ground === Tile.Window) {
            ctx.fillStyle = WINDOW_GLASS;
            ctx.fillRect(px + 4, py + 9, TILE_SIZE - 8, TILE_SIZE - 16);
          }
        } else if (ground === Tile.Void) {
          ctx.fillStyle = VOID_COLOR;
          ctx.fillRect(px, py, TILE_SIZE, TILE_SIZE);
        } else {
          ctx.fillStyle = FLOOR_COLORS[ground] ?? FLOOR_COLORS[Tile.Floor] ?? '#3b4150';
          ctx.fillRect(px, py, TILE_SIZE, TILE_SIZE);
          // Faint grid so movement reads clearly.
          ctx.strokeStyle = 'rgba(0,0,0,0.12)';
          ctx.lineWidth = 1;
          ctx.strokeRect(px + 0.5, py + 0.5, TILE_SIZE - 1, TILE_SIZE - 1);
        }

        const decor = map.decor[i] ?? 0;
        if (decor !== 0) this.drawDecor(decor, px, py);
      }
    }
  }

  private drawDecor(tile: number, px: number, py: number): void {
    const { ctx } = this;
    const color = DECOR_COLORS[tile] ?? '#6b7280';

    switch (tile) {
      case Tile.Plant: {
        ctx.fillStyle = '#5b4632';
        ctx.fillRect(px + 11, py + 20, 10, 9);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(px + 16, py + 15, 10, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case Tile.Chair: {
        ctx.fillStyle = color;
        this.roundRect(px + 7, py + 8, 18, 18, 5);
        ctx.fill();
        break;
      }
      case Tile.Whiteboard: {
        ctx.fillStyle = color;
        ctx.fillRect(px + 1, py + 6, TILE_SIZE - 2, TILE_SIZE - 12);
        ctx.strokeStyle = '#9aa0a6';
        ctx.lineWidth = 2;
        ctx.strokeRect(px + 1, py + 6, TILE_SIZE - 2, TILE_SIZE - 12);
        break;
      }
      case Tile.NoteBoard: {
        ctx.fillStyle = '#6f4f2a';
        ctx.fillRect(px, py + 3, TILE_SIZE, TILE_SIZE - 6);
        ctx.fillStyle = color;
        ctx.fillRect(px + 2, py + 5, TILE_SIZE - 4, TILE_SIZE - 10);
        break;
      }
      case Tile.Screen: {
        ctx.fillStyle = '#12161d';
        ctx.fillRect(px + 2, py + 4, TILE_SIZE - 4, TILE_SIZE - 8);
        ctx.fillStyle = color;
        ctx.fillRect(px + 4, py + 6, TILE_SIZE - 8, TILE_SIZE - 12);
        break;
      }
      default: {
        ctx.fillStyle = color;
        ctx.fillRect(px, py, TILE_SIZE, TILE_SIZE);
        ctx.fillStyle = 'rgba(255,255,255,0.06)';
        ctx.fillRect(px, py, TILE_SIZE, 4);
        break;
      }
    }
  }

  /**
   * Largest size at which a room's name still fits inside its walls.
   *
   * Measured rather than estimated from character count: "MEETING ROOM A" and
   * "LOUNGE" need very different sizes in rooms of the same width, and guessing
   * at average glyph width overflows on the long ones. Cached because the map
   * never changes and this would otherwise run every frame.
   */
  private labelSize(name: string, roomWidth: number): number {
    const key = `${name}@${roomWidth}`;
    const cached = this.labelSizes.get(key);
    if (cached !== undefined) return cached;

    const { ctx } = this;
    const probe = 100;
    ctx.save();
    ctx.font = `700 ${probe}px ui-sans-serif, system-ui, sans-serif`;
    (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = `${probe * 0.14}px`;
    const width = ctx.measureText(name.toUpperCase()).width || probe;
    ctx.restore();

    // Leave a margin so lettering never runs into the walls.
    const fitted = (probe * (roomWidth * 0.84)) / width;
    const size = Math.round(Math.max(14, Math.min(46, fitted)));
    this.labelSizes.set(key, size);
    return size;
  }

  /**
   * Room names as floor lettering — sized to the room and centred in it, the
   * way a name is painted across a sports court. Reads as part of the building
   * rather than a caption stuck to it.
   */
  private drawZoneLabels(map: OfficeMap, currentZoneId: string | null): void {
    const { ctx } = this;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (const zone of map.zones) {
      const rect = rectToPixels(zone.rect);
      const center = rectCenterPixels(zone.rect);
      const size = this.labelSize(zone.name, rect.w);

      ctx.font = `700 ${size}px ui-sans-serif, system-ui, sans-serif`;
      // letterSpacing is not in every lib.dom yet; harmless where unsupported.
      (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing =
        `${Math.round(size * 0.14)}px`;

      // The room you are in gets to be a little more present than the rest.
      ctx.fillStyle =
        zone.id === currentZoneId ? 'rgba(255,255,255,0.13)' : 'rgba(255,255,255,0.06)';
      ctx.fillText(zone.name.toUpperCase(), center.x, center.y);
    }

    (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = '0px';
    ctx.restore();
  }

  /**
   * Dim every room you are not standing in.
   *
   * Drawn last, over the avatars, so other rooms recede as a whole rather than
   * leaving lit people floating in a darkened room.
   *
   * Standing in a doorway belongs to no zone, and so dims everything — you are
   * not in any room, rather than in all of them. Each room eases toward its
   * target instead of snapping, so walking through a door reads as the light
   * coming up around you rather than the office blinking.
   */
  private drawZoneDimming(map: OfficeMap, currentZoneId: string | null): void {
    const { ctx } = this;
    const now = performance.now();
    const dt = this.lastDimAt === 0 ? 1 : Math.min((now - this.lastDimAt) / 1000, 0.1);
    this.lastDimAt = now;
    // Settles in roughly a tenth of a second: quick enough to feel immediate,
    // slow enough not to strobe while crossing a threshold.
    const ease = 1 - Math.exp(-dt * 12);

    ctx.save();
    for (const zone of map.zones) {
      const target = zone.id === currentZoneId ? 0 : ZONE_DIM;
      const previous = this.dimAlpha.get(zone.id) ?? target;
      const alpha = previous + (target - previous) * ease;
      this.dimAlpha.set(zone.id, alpha);

      if (alpha < 0.01) continue;
      const rect = rectToPixels(zone.rect);
      ctx.fillStyle = `rgba(9, 11, 16, ${alpha.toFixed(3)})`;
      ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
    }
    ctx.restore();
  }

  /** Boards get a sticky-note preview plus a highlight when reachable. */
  private drawSurfaces(map: OfficeMap, render: RenderContext): void {
    const { ctx } = this;
    for (const surface of map.surfaces) {
      const rect = rectToPixels(surface.rect);

      if (surface.kind === 'noteboard') {
        const notes = this.store.notesFor(surface.id);
        const horizontal = surface.rect.w >= surface.rect.h;
        const slots = Math.max(1, horizontal ? surface.rect.w : surface.rect.h);
        const shown = notes.slice(0, slots);
        for (let i = 0; i < shown.length; i++) {
          const note = shown[i];
          if (!note) continue;
          const sx = horizontal ? rect.x + i * TILE_SIZE + 8 : rect.x + 8;
          const sy = horizontal ? rect.y + 9 : rect.y + i * TILE_SIZE + 9;
          ctx.fillStyle = note.color;
          ctx.fillRect(sx, sy, 16, 14);
          ctx.strokeStyle = 'rgba(0,0,0,0.25)';
          ctx.lineWidth = 1;
          ctx.strokeRect(sx + 0.5, sy + 0.5, 15, 13);
        }
        if (notes.length > slots) {
          ctx.fillStyle = '#f5f5f5';
          ctx.font = '600 10px ui-sans-serif, system-ui, sans-serif';
          ctx.textAlign = 'right';
          ctx.fillText(`+${notes.length - slots}`, rect.x + rect.w - 3, rect.y + rect.h - 3);
        }
      }

      const isActive = render.activeSurfaceId === surface.id;
      const isNear = render.nearbySurface?.id === surface.id;
      if (isActive || isNear) {
        ctx.strokeStyle = isActive ? '#ffd966' : 'rgba(255, 217, 102, 0.55)';
        ctx.lineWidth = 2;
        ctx.strokeRect(rect.x - 2, rect.y - 2, rect.w + 4, rect.h + 4);
      }
    }
  }

  /** Show how far your voice/"nearby" chat carries. */
  private drawProximityRing(): void {
    const me = this.store.me;
    if (!me) return;
    const { ctx } = this;
    ctx.save();
    ctx.beginPath();
    ctx.arc(me.renderX, me.renderY, PROXIMITY_RADIUS, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.07)';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
  }

  private drawPlayers(): void {
    this.accumulateTravel();
    // Sort by y so avatars lower on screen overlap those above them.
    const players = [...this.store.players.values()].sort((a, b) => a.renderY - b.renderY);
    for (const player of players) this.drawPlayer(player, player.id === this.store.meId);
  }

  /**
   * Track how far each character has walked, to drive the walk cycle.
   *
   * Timing the animation instead would make a character crossing a doorway
   * look like it is moonwalking whenever the frame rate and the speed disagree.
   */
  private accumulateTravel(): void {
    for (const player of this.store.players.values()) {
      const last = this.lastPos.get(player.id);
      if (last) {
        const step = Math.hypot(player.renderX - last.x, player.renderY - last.y);
        // Ignore teleports, which would otherwise spin the legs.
        if (step < 40) this.travelled.set(player.id, (this.travelled.get(player.id) ?? 0) + step);
      }
      this.lastPos.set(player.id, { x: player.renderX, y: player.renderY });
    }
  }

  private drawPlayer(player: ClientPlayer, isMe: boolean): void {
    const { ctx } = this;
    const x = player.renderX;
    const y = player.renderY;

    // Walk frame comes from distance travelled, not wall time, so the stride
    // matches the speed rather than drifting against it.
    const travelled = this.travelled.get(player.id) ?? 0;
    const frame = walkFrame(travelled, player.moving);
    const sprite = characterSprite(player.appearance, player.facing, frame);

    const w = sprite.width;
    const h = sprite.height;
    // Feet at the position; the body extends upward from there.
    const left = Math.round(x - w / 2);
    const top = Math.round(y + PLAYER_RADIUS - h);

    ctx.fillStyle = 'rgba(0,0,0,0.28)';
    ctx.beginPath();
    ctx.ellipse(x, y + PLAYER_RADIUS - 2, PLAYER_RADIUS, PLAYER_RADIUS * 0.4, 0, 0, Math.PI * 2);
    ctx.fill();

    if (isMe) {
      // A ring on the floor rather than an outline on the sprite, which would
      // fight with the pixel art.
      ctx.strokeStyle = 'rgba(255,255,255,0.7)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(x, y + PLAYER_RADIUS - 2, PLAYER_RADIUS + 1, PLAYER_RADIUS * 0.45, 0, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(sprite, left, top);
    ctx.imageSmoothingEnabled = true;

    // Name beside the character rather than beneath it, so a crowd does not
    // become a stack of labels over each other's heads.
    ctx.font = '600 11px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const label = player.status ? `${player.name} · ${player.status}` : player.name;
    const width = ctx.measureText(label).width;
    const labelX = left + w + 5;
    const labelY = top + h * 0.35;

    ctx.fillStyle = 'rgba(12,14,20,0.72)';
    this.roundRect(labelX - 4, labelY - 8, width + 8, 16, 4);
    ctx.fill();
    ctx.fillStyle = isMe ? '#ffffff' : '#e8ebf2';
    ctx.fillText(label, labelX, labelY);
    ctx.textBaseline = 'alphabetic';

    if (player.bubble && player.bubble.until > performance.now()) {
      this.drawBubble(x, top - 6, player.bubble.text);
    }
  }

  private drawBubble(x: number, y: number, text: string): void {
    const { ctx } = this;
    ctx.font = '12px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'center';

    const maxWidth = 200;
    const words = text.split(' ');
    const lines: string[] = [];
    let line = '';
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (ctx.measureText(candidate).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    }
    if (line) lines.push(line);
    const shown = lines.slice(0, 3);
    if (shown.length === 0) return;

    const width = Math.max(...shown.map((l) => ctx.measureText(l).width)) + 16;
    const height = shown.length * 15 + 10;
    const top = y - height;

    ctx.fillStyle = 'rgba(240,242,248,0.95)';
    this.roundRect(x - width / 2, top, width, height, 7);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x - 5, top + height);
    ctx.lineTo(x + 5, top + height);
    ctx.lineTo(x, top + height + 6);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#1a1d26';
    shown.forEach((l, i) => ctx.fillText(l, x, top + 18 + i * 15));
  }

  private roundRect(x: number, y: number, w: number, h: number, r: number): void {
    const { ctx } = this;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }
}
